import {
  getLeoUuid,
  LeoUUID,
  getList,
  getInt32,
  getObject,
  getBoolean,
  getOptionalLeoUuid,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TableFilter, TableFilterDTO, TableFilterEnums } from "../types/tableFilter";
import { ProjectDetails, ProjectDetailsDTO } from "../types/projectDetails";
import { RespondentWithSurveyDetails, RespondentWithSurveyDetailsDTO } from "./respondentWithSurveyDetails";
import { RespondentColumnDetails, RespondentColumnDetailsDTO } from "../types/respondentColumnDetails";
import { SurveyIdAndName, SurveyIdAndNameDTO } from "../types/surveyIdAndName";
/* eslint-disable import/export */
export abstract class GetRespondentDetailsRPC implements
  LeoRPC<GetRespondentDetailsRPC.Request, GetRespondentDetailsRPC.Response, GetRespondentDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: GetRespondentDetailsRPC.Request
  ): Promise<LeoRPCResult<GetRespondentDetailsRPC.Response, GetRespondentDetailsRPC.Errors.Errors>>;
}

export namespace GetRespondentDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace SortOrder {
      export enum SortOrder {
        ASCENDING = "ASCENDING",
        DESCENDING = "DESCENDING",
      }
    
      export interface SortOrderDTO {
        case: string;
      }
    
      export const fromDTO = (dto: SortOrderDTO): SortOrder => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestSortOrderError(
            `Invalid DTO ${dto} provided for SortOrder.SortOrder`
          );
        }
        switch (dto.case) {
          case "ASCENDING":
            return SortOrder.ASCENDING;
          case "DESCENDING":
            return SortOrder.DESCENDING;
          default:
            throw new InvalidRequestSortOrderError(
              `Case ${dto.case} is not valid case of SortOrder.SortOrder`
            );
        }
      };
    
      export const toDTO = (sortOrder: SortOrder): SortOrderDTO => {
        const enumDTO = {
          case: SortOrder[sortOrder],
        };
        return enumDTO;
      };
    }
    
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace SortColumn {
      export enum SortColumn {
        ID = "ID",
        FIRST_NAME = "FIRST_NAME",
        LAST_NAME = "LAST_NAME",
        EMAIL_ID = "EMAIL_ID",
      }
    
      export interface SortColumnDTO {
        case: string;
      }
    
      export const fromDTO = (dto: SortColumnDTO): SortColumn => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestSortColumnError(
            `Invalid DTO ${dto} provided for SortColumn.SortColumn`
          );
        }
        switch (dto.case) {
          case "ID":
            return SortColumn.ID;
          case "FIRST_NAME":
            return SortColumn.FIRST_NAME;
          case "LAST_NAME":
            return SortColumn.LAST_NAME;
          case "EMAIL_ID":
            return SortColumn.EMAIL_ID;
          default:
            throw new InvalidRequestSortColumnError(
              `Case ${dto.case} is not valid case of SortColumn.SortColumn`
            );
        }
      };
    
      export const toDTO = (sortColumn: SortColumn): SortColumnDTO => {
        const enumDTO = {
          case: SortColumn[sortColumn],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    projectId: string;
    filters: TableFilterDTO[];
    pageIndex: number;
    itemsPerPage: number;
    sortOrder: RequestEnums.SortOrder.SortOrderDTO;
    sortColumn: RequestEnums.SortColumn.SortColumnDTO;
    isLive: boolean;
    spocId?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetRespondentDetailsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly filters: TableFilter[];
    readonly pageIndex: number;
    readonly itemsPerPage: number;
    readonly sortOrder: RequestEnums.SortOrder.SortOrder;
    readonly sortColumn: RequestEnums.SortColumn.SortColumn;
    readonly isLive: boolean;
    readonly spocId: LeoUUID | null;
  
    static readonly constraints = {
      "filters": {
        "maxSize": 6,
      },
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      },
    }
  
    constructor(
    projectId: LeoUUID, 
    filters: TableFilter[], 
    pageIndex: number, 
    itemsPerPage: number, 
    sortOrder: RequestEnums.SortOrder.SortOrder, 
    sortColumn: RequestEnums.SortColumn.SortColumn, 
    isLive: boolean, 
    spocId: LeoUUID | null = null
    ) {
      this.projectId = projectId;
      Request.validateFilters(filters);
      this.filters = filters;
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
      this.sortOrder = sortOrder;
      this.sortColumn = sortColumn;
      this.isLive = isLive;
      this.spocId = spocId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const filters = getList<TableFilterDTO, TableFilter>(
        dto,
        "filters",
        TableFilter.fromDTO,
        InvalidRequestFiltersError
      );
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      const sortOrder = RequestEnums.SortOrder.fromDTO(getObject(dto, "sortOrder", InvalidRequestSortOrderError));
      const sortColumn = RequestEnums.SortColumn.fromDTO(getObject(dto, "sortColumn", InvalidRequestSortColumnError));
      const isLive = getBoolean(dto, "isLive", InvalidRequestIsLiveError);
      const spocId = getOptionalLeoUuid(dto, "spocId", InvalidRequestSpocIdError);
      return new Request(
        projectId,
        filters,
        pageIndex,
        itemsPerPage,
        sortOrder,
        sortColumn,
        isLive,
        spocId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          filters: this.filters.map((e: TableFilter) => { return e.toDTO() }),
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage,
          sortOrder: RequestEnums.SortOrder.toDTO(this.sortOrder),
          sortColumn: RequestEnums.SortColumn.toDTO(this.sortColumn),
          isLive: this.isLive,
      };
      if (this.spocId !== undefined && this.spocId !== null){
        dto.spocId = this.spocId.toDTO();
      }
      return dto;
    }
    private static validateFilters = (filters: TableFilter[]): void => {
      if(filters.length > 6) {
        throw new InvalidRequestFiltersMaxSizeError(
          6,
          `Attribute filters is too long. Size is ${filters.length}. Max size is 6.`
        );
      }
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetRespondentDetailsRPC(projectId='${this.projectId}',filters='${this.filters}',pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}',sortOrder='${this.sortOrder}',sortColumn='${this.sortColumn}',isLive='${this.isLive}',spocId='${this.spocId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace RespondentUpdateStatus {
      export interface InProgressDTO {
        case: string;
      }
      
      export class InProgress {
        readonly isRespondentUpdateStatusInProgress: boolean = true;
      
      
      
      
      
        toDTO(): InProgressDTO {
          const dto: InProgressDTO = {
            case: "IN_PROGRESS",
          };
          return dto;
        }
        }
      
      InProgress.prototype.toString = function toString(): string {
        return `RespondentUpdateStatus.IN_PROGRESS()`;
      };
      
      
      export namespace CompletedEnums {
        export namespace Respondents {
          export interface NoRespondentsFoundDTO {
            case: string;
          }
          
          export class NoRespondentsFound {
            readonly isRespondentsNoRespondentsFound: boolean = true;
          
          
          
          
          
            toDTO(): NoRespondentsFoundDTO {
              const dto: NoRespondentsFoundDTO = {
                case: "NO_RESPONDENTS_FOUND",
              };
              return dto;
            }
            }
          
          NoRespondentsFound.prototype.toString = function toString(): string {
            return `Respondents.NO_RESPONDENTS_FOUND()`;
          };
          
          export interface NoMatchingRespondentsFoundDTO {
            case: string;
          }
          
          export class NoMatchingRespondentsFound {
            readonly isRespondentsNoMatchingRespondentsFound: boolean = true;
          
          
          
          
          
            toDTO(): NoMatchingRespondentsFoundDTO {
              const dto: NoMatchingRespondentsFoundDTO = {
                case: "NO_MATCHING_RESPONDENTS_FOUND",
              };
              return dto;
            }
            }
          
          NoMatchingRespondentsFound.prototype.toString = function toString(): string {
            return `Respondents.NO_MATCHING_RESPONDENTS_FOUND()`;
          };
          
          export interface RespondentsFoundDTO {
            case: string;
            respondents: RespondentWithSurveyDetailsDTO[];
            isViewingIndividualSurveyResponseAllowed: boolean;
          }
          
          export class RespondentsFound {
            readonly isRespondentsRespondentsFound: boolean = true;
          
            readonly respondents: RespondentWithSurveyDetails[];
            readonly isViewingIndividualSurveyResponseAllowed: boolean;
          
          
            constructor(
            respondents: RespondentWithSurveyDetails[], 
            isViewingIndividualSurveyResponseAllowed: boolean
            ) {
              this.respondents = respondents;
              this.isViewingIndividualSurveyResponseAllowed = isViewingIndividualSurveyResponseAllowed;
            }
          
            static fromDTO(dto: RespondentsFoundDTO): RespondentsFound {
              const respondents = getList<RespondentWithSurveyDetailsDTO, RespondentWithSurveyDetails>(
                dto,
                "respondents",
                RespondentWithSurveyDetails.fromDTO,
                InvalidResponseRespondentUpdateStatusCompletedRespondentsError
              );
              const isViewingIndividualSurveyResponseAllowed = getBoolean(dto, "isViewingIndividualSurveyResponseAllowed", InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundIsViewingIndividualSurveyResponseAllowedError);
              return new RespondentsFound(
                respondents,
                isViewingIndividualSurveyResponseAllowed
              );
            }
          
            toDTO(): RespondentsFoundDTO {
              const dto: RespondentsFoundDTO = {
                case: "RESPONDENTS_FOUND",
                  respondents: this.respondents.map((e: RespondentWithSurveyDetails) => { return e.toDTO() }),
                  isViewingIndividualSurveyResponseAllowed: this.isViewingIndividualSurveyResponseAllowed
              };
              return dto;
            }
            }
          
          RespondentsFound.prototype.toString = function toString(): string {
            return `Respondents.RESPONDENTS_FOUND(respondents='${this.respondents}',isViewingIndividualSurveyResponseAllowed='${this.isViewingIndividualSurveyResponseAllowed}')`;
          };
          
        
          export type Respondents = NoRespondentsFound | NoMatchingRespondentsFound | RespondentsFound;
        
          export type RespondentsDTO = NoRespondentsFoundDTO | NoMatchingRespondentsFoundDTO | RespondentsFoundDTO;
        
          export const fromDTO = (dto: RespondentsDTO): Respondents => {
            switch (dto.case) {
              case "NO_RESPONDENTS_FOUND":
                  return new NoRespondentsFound();
              case "NO_MATCHING_RESPONDENTS_FOUND":
                  return new NoMatchingRespondentsFound();
              case "RESPONDENTS_FOUND":
                  return RespondentsFound.fromDTO(dto as RespondentsFoundDTO);
              default:
                throw new InvalidResponseRespondentUpdateStatusCompletedRespondentsError(
                    `Case ${dto.case} is not valid case of Respondents.`
                );
            }
          };
        }
        
      }
      export interface CompletedDTO {
        case: string;
        totalItems: number;
        isProjectRespondentUniqueColumnPresent: boolean;
        respondents: CompletedEnums.Respondents.RespondentsDTO;
        respondentColumnDetails: RespondentColumnDetailsDTO[];
        surveyDetails: SurveyIdAndNameDTO[];
      }
      
      export class Completed {
        readonly isRespondentUpdateStatusCompleted: boolean = true;
      
        readonly totalItems: number;
        readonly isProjectRespondentUniqueColumnPresent: boolean;
        readonly respondents: CompletedEnums.Respondents.Respondents;
        readonly respondentColumnDetails: RespondentColumnDetails[];
        readonly surveyDetails: SurveyIdAndName[];
      
        static readonly constraints = {
          "totalItems": {
            "minValue": 0,
          },
        }
      
        constructor(
        totalItems: number, 
        isProjectRespondentUniqueColumnPresent: boolean, 
        respondents: CompletedEnums.Respondents.Respondents, 
        respondentColumnDetails: RespondentColumnDetails[], 
        surveyDetails: SurveyIdAndName[]
        ) {
          Completed.validateTotalItems(totalItems);
          this.totalItems = totalItems;
          this.isProjectRespondentUniqueColumnPresent = isProjectRespondentUniqueColumnPresent;
          this.respondents = respondents;
          this.respondentColumnDetails = respondentColumnDetails;
          this.surveyDetails = surveyDetails;
        }
      
        static fromDTO(dto: CompletedDTO): Completed {
          const totalItems = getInt32(dto, "totalItems", InvalidResponseRespondentUpdateStatusCompletedTotalItemsError);
          const isProjectRespondentUniqueColumnPresent = getBoolean(dto, "isProjectRespondentUniqueColumnPresent", InvalidResponseRespondentUpdateStatusCompletedIsProjectRespondentUniqueColumnPresentError);
          const respondents = CompletedEnums.Respondents.fromDTO(getObject(dto, "respondents", InvalidResponseRespondentUpdateStatusCompletedRespondentsError));
          const respondentColumnDetails = getList<RespondentColumnDetailsDTO, RespondentColumnDetails>(
            dto,
            "respondentColumnDetails",
            RespondentColumnDetails.fromDTO,
            InvalidResponseRespondentUpdateStatusCompletedRespondentColumnDetailsError
          );
          const surveyDetails = getList<SurveyIdAndNameDTO, SurveyIdAndName>(
            dto,
            "surveyDetails",
            SurveyIdAndName.fromDTO,
            InvalidResponseRespondentUpdateStatusCompletedSurveyDetailsError
          );
          return new Completed(
            totalItems,
            isProjectRespondentUniqueColumnPresent,
            respondents,
            respondentColumnDetails,
            surveyDetails
          );
        }
      
        toDTO(): CompletedDTO {
          const dto: CompletedDTO = {
            case: "COMPLETED",
              totalItems: this.totalItems,
              isProjectRespondentUniqueColumnPresent: this.isProjectRespondentUniqueColumnPresent,
              respondents: this.respondents.toDTO(),
              respondentColumnDetails: this.respondentColumnDetails.map((e: RespondentColumnDetails) => { return e.toDTO() }),
              surveyDetails: this.surveyDetails.map((e: SurveyIdAndName) => { return e.toDTO() })
          };
          return dto;
        }
        private static validateTotalItems = (totalItems: number): void => {
          if(totalItems < 0) {
            throw new InvalidResponseRespondentUpdateStatusCompletedTotalItemsMinValueError(
              0,
              `Attribute totalItems has value ${totalItems}. Min value is 0.`
            );
          }
        }
        
      }
      
      Completed.prototype.toString = function toString(): string {
        return `RespondentUpdateStatus.COMPLETED(totalItems='${this.totalItems}',isProjectRespondentUniqueColumnPresent='${this.isProjectRespondentUniqueColumnPresent}',respondents='${this.respondents}',respondentColumnDetails='${this.respondentColumnDetails}',surveyDetails='${this.surveyDetails}')`;
      };
      
    
      export type RespondentUpdateStatus = InProgress | Completed;
    
      export type RespondentUpdateStatusDTO = InProgressDTO | CompletedDTO;
    
      export const fromDTO = (dto: RespondentUpdateStatusDTO): RespondentUpdateStatus => {
        switch (dto.case) {
          case "IN_PROGRESS":
              return new InProgress();
          case "COMPLETED":
              return Completed.fromDTO(dto as CompletedDTO);
          default:
            throw new InvalidResponseRespondentUpdateStatusError(
                `Case ${dto.case} is not valid case of RespondentUpdateStatus.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    projectDetails: ProjectDetailsDTO;
    respondentUpdateStatus: ResponseEnums.RespondentUpdateStatus.RespondentUpdateStatusDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetRespondentDetailsResponse: boolean = true;
  
    readonly projectDetails: ProjectDetails;
    readonly respondentUpdateStatus: ResponseEnums.RespondentUpdateStatus.RespondentUpdateStatus;
  
  
    constructor(
    projectDetails: ProjectDetails, 
    respondentUpdateStatus: ResponseEnums.RespondentUpdateStatus.RespondentUpdateStatus
    ) {
      this.projectDetails = projectDetails;
      this.respondentUpdateStatus = respondentUpdateStatus;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const projectDetails = ProjectDetails.fromDTO(getObject(dto, "projectDetails", InvalidResponseProjectDetailsError));
      const respondentUpdateStatus = ResponseEnums.RespondentUpdateStatus.fromDTO(getObject(dto, "respondentUpdateStatus", InvalidResponseRespondentUpdateStatusError));
      return new Response(
        projectDetails,
        respondentUpdateStatus
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          projectDetails: this.projectDetails.toDTO(),
          respondentUpdateStatus: this.respondentUpdateStatus.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetRespondentDetails(projectDetails='${this.projectDetails}',respondentUpdateStatus='${this.respondentUpdateStatus}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX",
    InvalidProjectId = "INVALID_PROJECT_ID",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetRespondentDetailsRPC.INVALID_PAGE_INDEX()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetRespondentDetailsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `GetRespondentDetailsRPC.DELETED_COLUMN_FILTER()`;
    };
    

    export type Errors = InvalidPageIndex | InvalidProjectId | DeletedColumnFilter;
    export type ErrorsDTO = InvalidPageIndexDTO | InvalidProjectIdDTO | DeletedColumnFilterDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestFiltersError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersError";
  }
}

export class InvalidRequestFiltersMaxSizeError extends InvalidRequestFiltersError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFiltersMaxSizeError";
  }
}

export class InvalidRequestPageIndexError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidRequestSortOrderError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderError";
  }
}

export class InvalidRequestSortOrderAscendingError extends InvalidRequestSortOrderError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderAscendingError";
  }
}

export class InvalidRequestSortOrderDescendingError extends InvalidRequestSortOrderError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderDescendingError";
  }
}

export class InvalidRequestSortColumnError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnError";
  }
}

export class InvalidRequestSortColumnIdError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnIdError";
  }
}

export class InvalidRequestSortColumnFirstNameError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnFirstNameError";
  }
}

export class InvalidRequestSortColumnLastNameError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnLastNameError";
  }
}

export class InvalidRequestSortColumnEmailIdError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnEmailIdError";
  }
}

export class InvalidRequestIsLiveError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsLiveError";
  }
}

export class InvalidRequestSpocIdError extends GetRespondentDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdError";
  }
}

export class InvalidResponseProjectDetailsError extends GetRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectDetailsError";
  }
}

export class InvalidResponseRespondentUpdateStatusError extends GetRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusError";
  }
}

export class InvalidResponseRespondentUpdateStatusInProgressError extends InvalidResponseRespondentUpdateStatusError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusInProgressError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedError extends InvalidResponseRespondentUpdateStatusError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedTotalItemsError extends InvalidResponseRespondentUpdateStatusCompletedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedTotalItemsError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedTotalItemsMinValueError extends InvalidResponseRespondentUpdateStatusCompletedTotalItemsError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedTotalItemsMinValueError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedIsProjectRespondentUniqueColumnPresentError extends InvalidResponseRespondentUpdateStatusCompletedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedIsProjectRespondentUniqueColumnPresentError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsError extends InvalidResponseRespondentUpdateStatusCompletedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsNoRespondentsFoundError extends InvalidResponseRespondentUpdateStatusCompletedRespondentsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsNoRespondentsFoundError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsNoMatchingRespondentsFoundError extends InvalidResponseRespondentUpdateStatusCompletedRespondentsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsNoMatchingRespondentsFoundError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundError extends InvalidResponseRespondentUpdateStatusCompletedRespondentsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundRespondentsError extends InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundRespondentsError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundIsViewingIndividualSurveyResponseAllowedError extends InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentsRespondentsFoundIsViewingIndividualSurveyResponseAllowedError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedRespondentColumnDetailsError extends InvalidResponseRespondentUpdateStatusCompletedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedRespondentColumnDetailsError";
  }
}

export class InvalidResponseRespondentUpdateStatusCompletedSurveyDetailsError extends InvalidResponseRespondentUpdateStatusCompletedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateStatusCompletedSurveyDetailsError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends GetRespondentDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

